import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Slider from 'react-slick';
import axios from 'axios';
import EditProductModal from './EditProductModal';
import './ProductCard.css';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

const API_BASE_URL = '/api/products';
const priceLabels = {
  priceOneHour: 'За 1 час',
  priceTwoHours: 'За 2 часа',
  priceTravelOneHour: 'У тебя (1ч)',
  priceTravelTwoHours: 'У тебя (2ч)',
  priceWholeNight: 'Всю ночь',
};

const formatPhoneNumber = (phone) => {
  // Removes all non-numeric characters and formats for tel and WhatsApp
  const cleanPhone = phone.replace(/\D+/g, '');
  return cleanPhone.startsWith('8')
    ? `+7${cleanPhone.slice(1)}`
    : cleanPhone.startsWith('7')
      ? `+7${cleanPhone.slice(1)}`
      : `+${cleanPhone}`;
};

const ProductCard = ({ product, onUpdate, isAdmin = false, isOwner = false, isDashboard = true }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(product.isVisible);
  const [isVerified, setIsVerified] = useState(product.isVerified);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [isLoading, setIsLoading] = useState(false); // For requests
  const navigate = useNavigate();

  const remainingTime = product.statusExpiration
    ? Math.max(0, new Date(product.statusExpiration) - new Date())
    : null;
  const daysLeft = remainingTime ? Math.ceil(remainingTime / (1000 * 60 * 60 * 24)) : null;

  const productCardClass = `product-card ${product.status === 'Стандарт' ? 'product_card_standart' : ''
    } ${product.status === 'Премиум' ? 'product_card_premium' : ''}`;

  const sendRequest = async (url, method = 'PUT', data = {}) => {
    try {
      setIsLoading(true);
      const response = await axios({
        url: `${API_BASE_URL}/${product.id}/${url}`,
        method,
        data,
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      console.log(`✅ Успешно выполнено: ${url}`, response.data);
      onUpdate(); // Trigger parent component update
    } catch (error) {
      console.error(`❌ Ошибка при выполнении запроса ${url}:`, error.response?.data || error.message);
      alert(`Ошибка: ${error.response?.data?.message || 'Не удалось выполнить запрос'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const changeStatus = async (newStatus, duration) => {
    setLoadingStatus(true);
    try {
      await axios.put(
        `${API_BASE_URL}/${product.id}/status`,
        { status: newStatus, duration },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      onUpdate();
      alert('Статус успешно обновлён!');
    } catch (err) {
      console.error('Ошибка при смене статуса:', err);
      alert('Не удалось обновить статус. Повторите попытку.');
    } finally {
      setLoadingStatus(false);
    }
  };

  const handleDelete = async (event) => {
    event.stopPropagation();
    if (window.confirm('Вы уверены, что хотите удалить этот товар?')) {
      await sendRequest('', 'DELETE');
    }
  };

  const toggleVisibility = async (event) => {
    event.stopPropagation();
    await sendRequest('visibility', 'PUT', { isVisible: !isVisible });
    setIsVisible(!isVisible);
  };

  const handleApproval = async (isApprove) => {
    await sendRequest(isApprove ? 'approve' : 'reject');
    setIsVerified(isApprove);
  };

  const renderPrices = (prices) => {
    const parsedPrices = typeof prices === 'string' ? JSON.parse(prices) : prices;
    return (
      <div className="product-card__prices">
        {Object.entries(parsedPrices).map(([key, value]) => (
          <div className="product-card__prices_t" key={key}>
            <p>{priceLabels[key]}</p>
            <p>{value} руб.</p>
          </div>
        ))}
      </div>
    );
  };

  const handleCardClick = () => navigate(`/products/${product.id}`);
  const openEditModal = (event) => {
    event.stopPropagation();
    setIsModalOpen(true);
  };
  const closeEditModal = () => setIsModalOpen(false);

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
  };

  const formattedPhone = formatPhoneNumber(product.phone);
  const whatsappLink = `https://wa.me/${formattedPhone.replace('+', '')}`;

  return (
    <div className={productCardClass}>
      <h3>{product.title}</h3>
      {/* Image Carousel */}
      <div className="product-card__image" onClick={handleCardClick}>
        <Slider {...sliderSettings}>
          <div>
            <img
              src={`/uploads/${product.mainImage}`}
              alt="Основное изображение"
              className="carousel-image"
            />
          </div>
          {product.images &&
            product.images.map((image, index) => (
              <div key={index}>
                <img
                  src={`/uploads/${image}`}
                  alt={`Изображение ${index + 1}`}
                  className="carousel-image"
                />
              </div>
            ))}
        </Slider>
      </div>
      <div className="product-card__info">
        {renderPrices(product.prices)}
        <div className="phone">
         <p> {formattedPhone} </p>
          <div className="contact-buttons">
            <a href={`tel:${formattedPhone}`} className="btn contact-btn call-btn">
              Позвонить
            </a>
            <a href={whatsappLink} className="btn contact-btn whatsapp-btn" target="_blank" rel="noopener noreferrer">
              Написать 
            </a>
          </div>
        </div>


        {isDashboard && (
          <div className="product-card__actions">
            {isAdmin && (
              <>
                <button
                  onClick={() => handleApproval(true)}
                  disabled={isVerified || isLoading}
                  className="glow-on-hover"
                >
                  Одобрить
                </button>
                <button
                  onClick={() => handleApproval(false)}
                  disabled={!isVerified || isLoading}
                  className="glow-on-hover"
                >
                  Отклонить
                </button>
              </>
            )}
            <div className="product-card__status">
              <p>Статус: {product.status}</p>
              {daysLeft !== null && <p>Осталось дней: {daysLeft}</p>}
              <p>Видимость: {isVisible ? 'Показана' : 'Скрыт'}</p>
              <p>Проверено: {isVerified ? 'Одобрен' : 'Не одобрен'}</p>
            </div>
            <div className="product-card__status-actions">
              <button onClick={() => changeStatus('Обычный')} disabled={loadingStatus} className="glow-on-hover">
                Установить Обычный
              </button>
              <button onClick={() => changeStatus('Стандарт', 7)} disabled={loadingStatus} className="glow-on-hover">
                Установить Стандарт (7 дней)
              </button>
              <button onClick={() => changeStatus('Премиум', 30)} disabled={loadingStatus} className="glow-on-hover">
                Установить Премиум (30 дней)
              </button>
              {loadingStatus && <p>Обновление статуса...</p>}
            </div>
            <button onClick={openEditModal} className="glow-on-hover">
              Редактировать
            </button>
            <button onClick={handleDelete} className="glow-on-hover" disabled={isLoading}>
              Удалить
            </button>
            <button onClick={toggleVisibility} disabled={isLoading} className="glow-on-hover">
              {isVisible ? 'Скрыть' : 'Показать'}
            </button>
          </div>
        )}
      </div>
      {isModalOpen && (
        <EditProductModal product={product} onClose={closeEditModal} onUpdate={onUpdate} />
      )}
    </div>
  );
};

export default ProductCard;
